import { LoginBody } from '@back-office/http';
import { useLocalStore } from 'mobx-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useInterval } from 'react-use';
import { EnterOTPProps, OtpVia } from '.';
import {
  SMS_OTP_TIMEOUT_IN_SEC,
  TIMER_DELAY_MILLISECOND,
} from '../../../constants';
import { LoginStep } from '../../../forms';
import useInject from '../../../hooks/useInject';

type HistoryLocationState = {
  token: string;
  password: string;
  isRegister: boolean;
};

type OtpModeMap = {
  [OtpVia.EMAIL]: {
    receiverText: string;
    timeToShowToggleBtn: number;
    toggleOtpModeText: string;
    toggleValue: OtpVia;
  };
  [OtpVia.SMS]: {
    receiverText: string;
    timeToShowToggleBtn: number;
    toggleOtpModeText: string;
    toggleValue: OtpVia;
  };
};

type UseViewModelReturn = {
  handleOtpInputChange: (otpCode: string) => void;
  handleResendOtp: (toggleBeforeResend?: boolean) => Promise<void>;
  otpMode: OtpVia;
  otpModeMap: OtpModeMap;
  otpRef: string;
  state: {
    error: boolean;
    otp: string;
    isRunning: boolean;
    countdownTime: number;
  };
};

const useViewModel = ({ setAuth }: EnterOTPProps): UseViewModelReturn => {
  const [otpMode, setOtpMode] = useState<OtpVia>(OtpVia.EMAIL);
  const { t } = useTranslation();

  const history = useHistory<HistoryLocationState>();
  const {
    setLoginStep,
    verifyOTP,
    login,
    otp,
    register,
    resendOtp,
    requestOTPViaSms,
  } = useInject((store) => store);

  const state = useLocalStore(() => ({
    error: false,
    otp: '',
    isRunning: true,
    countdownTime: otp.expiredSecond,
  }));

  useInterval(
    () => {
      state.countdownTime -= 1;

      if (state.countdownTime === 0) {
        state.isRunning = false;
      }
    },
    state.isRunning ? TIMER_DELAY_MILLISECOND : null
  );
  const maskedPhoneNumber = (phone: string): string =>
    phone ? `** *****${phone.slice(-4)}` : phone;

  const otpModeMap = useMemo<OtpModeMap>(
    () => ({
      [OtpVia.EMAIL]: {
        receiverText: otp.email,
        timeToShowToggleBtn: otp.expiredSecond - SMS_OTP_TIMEOUT_IN_SEC,
        toggleOtpModeText: t('otp_input.receive_otp_sms'),
        toggleValue: OtpVia.SMS,
      },
      [OtpVia.SMS]: {
        receiverText: maskedPhoneNumber(otp?.phone),
        timeToShowToggleBtn: 0,
        toggleOtpModeText: t('otp_input.receive_otp_email'),
        toggleValue: OtpVia.EMAIL,
      },
    }),
    [otp.email, otp.expiredSecond, otp.phone, t]
  );

  const handleRedirect = (response: LoginBody): void => {
    if (response.mids.length > 1) {
      return setLoginStep(LoginStep.SelectMerchant);
    }
    return setAuth(0);
  };

  const signIn = async (otpToken: string): Promise<void> => {
    const response: LoginBody = await login({
      authToken: otp.authToken,
      otpToken,
    });
    handleRedirect(response);
  };

  const signUp = async (otpToken: string): Promise<void> => {
    const response: LoginBody = await register({
      authToken: otp.authToken,
      otpToken,
    });
    handleRedirect(response);
  };

  const request = async (otpCode: string): Promise<void> => {
    try {
      const email = otp.email || history.location?.state?.email;
      const verifyOtpRequest = {
        otpId: otp.otpId,
        otpRef: otp.otpRef,
        otpCode,
        email: otpMode === OtpVia.SMS ? email : undefined,
      };
      const otpToken = await verifyOTP(verifyOtpRequest, otpMode);
      if (history.location?.state?.isRegister) {
        signUp(otpToken);
      } else {
        signIn(otpToken);
      }
    } catch {
      state.error = true;
    }
  };

  const handleOtpInputChange = (otpCode: string): void => {
    state.error = false;
    state.otp = otpCode;
    if (state.otp.length === 6) {
      request(otpCode);
    }
  };

  const resendOtpEmail = async (): Promise<void> => {
    state.otp = '';
    await resendOtp();
    state.countdownTime = otp.expiredSecond;
    state.isRunning = true;
  };

  const resendOtpSms = async (): Promise<void> => {
    state.otp = '';
    await requestOTPViaSms(otp.email);
    state.countdownTime = SMS_OTP_TIMEOUT_IN_SEC;
    state.isRunning = true;
  };

  const handleResendOtp = (toggleBeforeResend?: boolean): Promise<void> => {
    let newOtpMode = otpMode;
    if (toggleBeforeResend) {
      newOtpMode = otpModeMap[otpMode].toggleValue;
      setOtpMode(newOtpMode);
    }
    if (newOtpMode === OtpVia.EMAIL) {
      return resendOtpEmail();
    }
    return resendOtpSms();
  };

  return {
    handleOtpInputChange,
    handleResendOtp,
    otpMode,
    otpModeMap,
    state,
    otpRef: otp.otpRef,
  };
};

export default useViewModel;
